// Types pour le système de subscription Stripe
// Aligné avec US-PAY-2: Structure de Données Subscription
//
// ⚠️ ARCHITECTURE (Migration 2025):
// La SOURCE UNIQUE DE VÉRITÉ pour les données de subscription est:
//   Firestore: subscriptions/{userId}
//
// Les champs subscriptionTier/subscriptionStatus/cvCount dans users/{userId}
// sont OBSOLÈTES et ne doivent plus être utilisés.
//
// Structure des données:
// - subscriptions/{userId}: tier, status, cvCount, billing info (SOURCE UNIQUE)
// - users/{userId}: stripeCustomerId (pour lookups), role, licenseOverride

import { Timestamp } from 'firebase/firestore';

export type SubscriptionTier = 'free' | 'trial_7d' | 'pro_monthly' | 'pro_3months' | 'pro_annual' | 'premium_monthly' | 'business' | 'one_time';
// 'unpaid' : statut Stripe atteint quand toutes les tentatives de paiement
// de la dunning ont échoué (après past_due). Comme past_due, il NE donne PAS
// accès aux features premium (cf. useSubscription.license / premiumGuard).
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trialing' | 'incomplete' | 'inactive';

/**
 * Interface pour le document subscriptions/{userId}
 * C'est la SOURCE UNIQUE DE VÉRITÉ pour:
 * - tier (type d'abonnement)
 * - status (état de l'abonnement)
 * - metadata.cvCount (compteur de CVs)
 * - billing info (dates, annulation, etc.)
 */
export interface Subscription {
  id: string; // Document ID (= userId)
  userId: string; // Référence à users/{userId}
  status: SubscriptionStatus;
  tier: SubscriptionTier;
  stripeCustomerId: string; // cus_xxx
  stripeSubscriptionId: string; // sub_xxx
  stripePriceId: string; // price_xxx
  currentPeriodStart: Timestamp;
  currentPeriodEnd: Timestamp;
  cancelAtPeriodEnd: boolean;
  trialEnd?: Timestamp;
  metadata: {
    cvCount: number; // Compteur d'usage (SOURCE UNIQUE)
    lastCvCreated?: Timestamp;
    promoCode?: string;
    cvCountResetDate?: Timestamp; // Date du dernier reset (plans mensuels)
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

/**
 * @deprecated Ces champs ne sont plus stockés dans users/{userId}.
 * La SOURCE UNIQUE DE VÉRITÉ est maintenant subscriptions/{userId}.
 *
 * Cette interface est conservée pour rétrocompatibilité mais ne doit plus être utilisée.
 * Utilisez plutôt l'interface `Subscription` pour les données de subscription.
 *
 * Migration (2025):
 * - tier, status, cvCount → subscriptions/{userId}
 * - stripeCustomerId → subscriptions/{userId} (et users/{userId} pour lookup uniquement)
 * - cvCount → subscriptions/{userId}.metadata.cvCount
 */
export interface UserSubscriptionFields {
  /** @deprecated Utiliser subscriptions/{userId}.tier */
  subscriptionTier: SubscriptionTier;
  /** @deprecated Utiliser subscriptions/{userId}.status */
  subscriptionStatus: SubscriptionStatus;
  stripeCustomerId?: string;
  trialStartDate?: Timestamp;
  trialEndDate?: Timestamp;
  /** @deprecated Utiliser subscriptions/{userId}.metadata.cvCount */
  cvCount: number;
  cvCountResetDate?: Timestamp;
}

export interface PaymentHistory {
  id: string; // Document ID
  userId: string;
  stripeInvoiceId: string;
  amount: number; // en centimes
  currency: string; // 'chf'
  status: 'paid' | 'failed' | 'refunded';
  paidAt?: Timestamp;
  failedReason?: string;
  metadata: Record<string, any>;
  createdAt: Timestamp;
}

export interface SubscriptionFeatures {
  maxCVs: number; // -1 pour illimité
  hasUnlimitedCVs: boolean;
  hasPremiumTemplates: boolean;
  hasATSOptimization: boolean;
  hasWordExport: boolean;
  hasPrioritySupport: boolean;
  hasAPIAccess: boolean;
  /** Voir le flux d'offres scannées 3x/jour. Inclus pour tous (gratuit en lecture seule). */
  hasJobSuggestions: boolean;
  /** Édition des critères de recherche persistés + Mode Autopilote
   *  (création/envoi automatique de candidatures). Premium uniquement. */
  hasAutopilot: boolean;
}

export interface SubscriptionUsage {
  cvCount: number;
  cvsRemaining: number; // -1 pour illimité
}

export interface SubscriptionBilling {
  currentPeriodEnd?: Date;
  cancelAtPeriodEnd: boolean;
  daysUntilRenewal?: number;
}

/**
 * Override de licence manuel par l'admin
 * Permet d'activer une licence pour un utilisateur sans passer par Stripe
 */
export interface LicenseOverride {
  enabled: boolean;
  tier: SubscriptionTier; // 'trial_7d' | 'pro_monthly' | 'pro_3months'
  note?: string; // Note interne pour l'admin
  updatedAt: Timestamp;
  updatedBy: string; // Admin ID
  expiresAt?: Timestamp; // Optionnel: expiration de l'override
}

/**
 * Informations sur la licence effective de l'utilisateur
 */
export interface LicenseInfo {
  isActive: boolean; // True si l'utilisateur a une licence active (Stripe ou override)
  source: 'stripe' | 'admin' | 'one_time' | 'none'; // Source de la licence ('one_time' = achat paiement unique 49)
  tierEffective: SubscriptionTier; // Tier effectif (peut venir de Stripe ou override)
  statusEffective: SubscriptionStatus | 'inactive'; // Status effectif
}

export interface SubscriptionState {
  tier: SubscriptionTier;
  status: SubscriptionStatus | 'loading';
  features: SubscriptionFeatures;
  usage: SubscriptionUsage;
  billing: SubscriptionBilling;
  hasHadTrial: boolean; // True si l'utilisateur a déjà eu une période d'essai
  license: LicenseInfo; // Informations sur la licence effective
  actions: {
    upgrade: (tier: SubscriptionTier) => Promise<void>;
    manageBilling: () => Promise<void>;
    cancelSubscription: () => Promise<void>;
  };
}

// Configuration des plans (prix en CHF - Nouveau pricing 2026 freemium)
export const SUBSCRIPTION_PLANS = {
  free: {
    tier: 'free' as SubscriptionTier,
    name: 'Gratuit',
    price: 0,
    currency: 'CHF',
    maxCVs: 3, // 3 CV gratuits (freemium — s'ancrer avant le paywall ; export PDF bloqué)
    features: {
      maxCVs: 3, // ⚠️ aligné avec le back `premiumGuard.checkQuota` (limits.free)

      hasUnlimitedCVs: false,
      hasPremiumTemplates: true, // tous les templates accessibles (visibles dès /score)
      // `false` — alignement sur ce que le backend applique RÉELLEMENT :
      // `jobAnalysisUnified` exige `validatePremiumAccess(trial_7d)` depuis
      // toujours. Le plan gratuit annonçait donc une analyse d'offre que le
      // serveur lui refusait, et le paywall la revendait comme un avantage payant
      // que le gratuit était censé déjà avoir. Le flag dit maintenant la vérité.
      hasATSOptimization: false,
      hasWordExport: false,      // export PDF = payant
      hasPrioritySupport: false,
      hasAPIAccess: false,
      hasJobSuggestions: true,   // visible en lecture seule (pas d'édition critères)
      hasAutopilot: false,       // autopilote = premium
    },
  },
  trial_7d: {
    tier: 'trial_7d' as SubscriptionTier,
    name: 'Essai gratuit 7 jours',
    price: 0, // Gratuit — CB requise, auto-renouvellement en pro_monthly après 7j
    currency: 'CHF',
    duration: '7 jours',
    maxCVs: -1, // Illimité pendant le trial
    features: {
      maxCVs: -1,
      hasUnlimitedCVs: true,
      hasPremiumTemplates: true,
      hasATSOptimization: true,
      hasWordExport: true,
      hasPrioritySupport: true,
      hasAPIAccess: false,
      hasJobSuggestions: true,
      hasAutopilot: false, // autopilote = palier Autopilote (premium_monthly) uniquement
    },
  },
  pro_monthly: {
    tier: 'pro_monthly' as SubscriptionTier,
    name: 'Mensuel',
    price: 29.90, // CHF/mois
    currency: 'CHF',
    maxCVs: -1, // Illimité
    features: {
      maxCVs: -1,
      hasUnlimitedCVs: true,
      hasPremiumTemplates: true,
      hasATSOptimization: true,
      hasWordExport: true,
      hasPrioritySupport: true,
      hasAPIAccess: false,
      hasJobSuggestions: true,
      hasAutopilot: false, // autopilote = palier Autopilote (premium_monthly) uniquement
    },
  },
  pro_3months: {
    tier: 'pro_3months' as SubscriptionTier,
    name: '3 Mois',
    price: 59.90, // CHF pour 3 mois (~20/mois, -33% vs mensuel)
    currency: 'CHF',
    duration: '3 mois',
    maxCVs: -1, // Illimité
    features: {
      maxCVs: -1,
      hasUnlimitedCVs: true,
      hasPremiumTemplates: true,
      hasATSOptimization: true,
      hasWordExport: true,
      hasPrioritySupport: true,
      hasAPIAccess: false,
      hasJobSuggestions: true,
      hasAutopilot: false, // autopilote = palier Autopilote (premium_monthly) uniquement
    },
  },
  // Palier « Autopilote » — re-gating de l'autopilote (pas de nouvelle feature).
  // ⚠️ Stripe : Julien doit créer le price (99 CHF/mois récurrent) et définir
  // l'env/secret STRIPE_PRICE_PREMIUM_MONTHLY. Aucun price n'est créé ici.
  premium_monthly: {
    tier: 'premium_monthly' as SubscriptionTier,
    name: 'Autopilote',
    price: 99, // CHF/mois
    currency: 'CHF',
    maxCVs: -1, // Illimité
    features: {
      maxCVs: -1,
      hasUnlimitedCVs: true,
      hasPremiumTemplates: true,
      hasATSOptimization: true,
      hasWordExport: true,
      hasPrioritySupport: true,
      hasAPIAccess: false,
      hasJobSuggestions: true,
      hasAutopilot: true, // seul palier avec l'autopilote (hors business)
    },
  },
  // Paiement unique « Accès complet » 49 CHF — PAS un abonnement.
  // L'entitlement est accordé par le webhook Stripe via licenseOverride
  // (tier pro_monthly, 90 jours). Features identiques au Pro mensuel.
  one_time: {
    tier: 'one_time' as SubscriptionTier,
    name: 'Accès complet',
    price: 49, // CHF, paiement unique
    currency: 'CHF',
    interval: 'one-time',
    duration: '90 jours',
    maxCVs: -1, // Illimité
    features: {
      maxCVs: -1,
      hasUnlimitedCVs: true,
      hasPremiumTemplates: true,
      hasATSOptimization: true,
      hasWordExport: true,
      hasPrioritySupport: true,
      hasAPIAccess: false,
      hasJobSuggestions: true,
      hasAutopilot: false, // autopilote = palier Autopilote (premium_monthly) uniquement
    },
  },
  // pro_annual : palier annuel legacy. PAS d'autopilote (re-gating Autopilote).
  pro_annual: {
    tier: 'pro_annual' as SubscriptionTier,
    name: 'Annuel',
    price: 199, // CHF/an
    currency: 'CHF',
    duration: '12 mois',
    maxCVs: -1, // Illimité
    features: {
      maxCVs: -1,
      hasUnlimitedCVs: true,
      hasPremiumTemplates: true,
      hasATSOptimization: true,
      hasWordExport: true,
      hasPrioritySupport: true,
      hasAPIAccess: false,
      hasJobSuggestions: true,
      hasAutopilot: false, // autopilote = palier Autopilote (premium_monthly) uniquement
    },
  },
  // business : palier interne au-dessus d'Autopilote. Conserve l'autopilote.
  business: {
    tier: 'business' as SubscriptionTier,
    name: 'Business',
    price: 0, // tarif négocié / interne
    currency: 'CHF',
    maxCVs: -1, // Illimité
    features: {
      maxCVs: -1,
      hasUnlimitedCVs: true,
      hasPremiumTemplates: true,
      hasATSOptimization: true,
      hasWordExport: true,
      hasPrioritySupport: true,
      hasAPIAccess: true,
      hasJobSuggestions: true,
      hasAutopilot: true, // business >= Autopilote
    },
  },
} as const;

// Codes promo - Nouveau pricing CHF 2026
export const PROMO_CODES = {
  FITD2025: {
    code: 'FITD2025',
    discount: 0,
    description: 'Code promo legacy',
  },
} as const;
